import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { Account } from "@somasi/shared";
import { ADMIN_ROLES, PAYROLL_READ_ROLES, PAYROLL_WRITE_ROLES } from "@somasi/shared";
import { api } from "@/integrations/aws/resources";
import { PageHeader, type Crumb } from "@/components/app-shell";
import { PersonForm } from "@/components/person-form";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * `/people/new` — add somebody, with their terms and their access, in one go.
 *
 * `?account=<sub>` adopts an existing sign-in that has nobody behind it: the
 * form opens pre-filled from the account and creates the record it was missing.
 * Everything else is an ordinary new person.
 */
export const Route = createFileRoute("/_authenticated/people/new")({
  /*
   * Returned as a partial rather than with an explicit `undefined`, so the key
   * is optional in the type and `<Link to="/people/new">` needs no search at
   * all — the ordinary way in is without one.
   */
  validateSearch: (search: Record<string, unknown>): { account?: string } =>
    typeof search.account === "string" ? { account: search.account } : {},
  head: () => ({ meta: [{ title: "Add a person — Somasi Ops" }] }),
  component: NewPersonPage,
});

const ADD_CRUMBS: Crumb[] = [{ label: "People", to: "/people" }, { label: "Add a person" }];

function NewPersonPage() {
  const { account } = Route.useSearch();
  const navigate = useNavigate();
  const { hasAny } = useAuth();

  const canWrite = hasAny(PAYROLL_WRITE_ROLES);
  const canSeeTerms = hasAny(PAYROLL_READ_ROLES);
  const canGrant = hasAny(ADMIN_ROLES);

  /*
   * The account being adopted, looked up from the register's own report of the
   * ones with nobody behind them. Asked for here rather than passed through the
   * link, so the page works when somebody opens the URL directly.
   */
  const adopting = useQuery({
    queryKey: ["people", "unlinked", account],
    queryFn: async () => {
      const result = await api.employees.people();
      if (result.error) throw new Error(result.error.message);
      return result.unlinked.find((a: Account) => a.id === account) ?? null;
    },
    enabled: !!account,
  });

  const back = () => void navigate({ to: "/people" });

  if (!canWrite) {
    return (
      <div className="space-y-6">
        <PageHeader title="Add a person" breadcrumbs={ADD_CRUMBS} />
        <Alert variant="destructive">
          <AlertTitle>Access restricted</AlertTitle>
          <AlertDescription>
            Only the payroll roles may add somebody to the register.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (account && adopting.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Add a person" breadcrumbs={ADD_CRUMBS} />
        <p className="text-muted-foreground text-sm">Reading the account…</p>
      </div>
    );
  }

  if (account && !adopting.data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Add a person" breadcrumbs={ADD_CRUMBS} />
        <Alert variant="destructive">
          <AlertTitle>That account has no record to create</AlertTitle>
          <AlertDescription>
            It is either gone from the user pool or somebody has already been given it.
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={back}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to People
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={adopting.data ? "Who is this account?" : "Add a person"}
        breadcrumbs={ADD_CRUMBS}
        description={
          adopting.data
            ? "An account that can already sign in, and the record it has been missing."
            : "Their record, their terms, and what they may do in the admin panel."
        }
        actions={
          <Button variant="outline" asChild>
            <Link to="/people">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Link>
          </Button>
        }
      />

      <PersonForm
        person={null}
        adopting={adopting.data ?? null}
        canSeeTerms={canSeeTerms}
        canGrant={canGrant}
        onCancel={back}
        onSaved={(saved) =>
          void navigate({ to: "/people/$personId", params: { personId: saved.id } })
        }
      />
    </div>
  );
}
